import { useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../../../BaseComponents/Loader';
import Select from '../../../BaseComponents/Select';
import AutoSelectAppointment from './AutoSelectAppointment';
import { RootState } from '../../../../store/store';
import { useFormikContext } from 'formik';
import './TestResultsModal.scss';
import SingleBiomarkerTestResult from './SingleBiomarkerTestResult';
import Button from '../../../BaseComponents/Button';
import {
  setIsSelectHL7ModalOpened,
  setMemberBiomarkersResultsByAppointment,
  setSelectedAppointmentIdForTestResults
} from '../../../../store/memberSlice';
import {
  deleteBiomarkerResult,
  getBiomarkersIdsExistingByAppointment
} from '../../../../api/biomarkers';
import {
  changeDeleteSingleBiomarkerResultModalVisibility,
  setSelectedTestResultIdToDelete,
} from '../../../../store/alertsSlice';
import ConfirmModal from '../../../BaseComponents/Modal/ConfirmModal';
import SelectHL7Modal from './SelectHL7Modal';
import { parseSimpleBiomarkerFile } from '../../../../utils/simpleCsvParser';
import { filterActiveBiomarkers } from '../../../../utils/biomarkers';
export interface TestResultsModalFormTypes {
  appointment: {
    key: any;
    label: any;
  };
  biomarkers: {
    id: any;
    name: {
      key: any;
      value: any;
    };
    summary: any;
    value: any;
    measurement_unit: any;
    biomarker_result_id?: any;
  }[];
}

interface TestResultsModalFormProps {
  isSubmitting: boolean;
  isFormError: any;
  isOpen: boolean;
  mode: 'create' | 'update';
  selectedAppointmentIdForTestResults?: number;
}

const TestResultsModalForm = ({
  isSubmitting,
  isFormError,
  isOpen,
  mode,
  selectedAppointmentIdForTestResults
}: TestResultsModalFormProps) => {
  const dispatch = useDispatch();
  const [unmappedTests, setUnmappedTests] = useState<any[]>([]);
  const { values, setFieldValue, handleSubmit } =
    useFormikContext<TestResultsModalFormTypes>();

  const [openedBiomarker, setOpenedBiomarker] = useState<string | null>(null);
  const [isTestsResultsLoading, setIsTestsResultsLoading] =
    useState<boolean>(false);
  const [isUploadingCSV, setIsUploadingCSV] = useState<boolean>(false);
  const csvFileInputRef = useRef<HTMLInputElement>(null);

  const {
    appointmentsListForTestsResults,
    appointmentIdToEditForTestResults,
    isSelectHL7ModalOpened,
    memberBiomarkersExistingIdsByAppointment
  } = useSelector((state: RootState) => state.member);

  const { biomarkersList } = useSelector((state: RootState) => state.app);
  const activeBiomarkersList = useMemo(
    () => filterActiveBiomarkers(biomarkersList),
    [biomarkersList]
  );
  const {
    isDeleteSingleBiomarkerResultModalVisible,
    selectedTestResultIdToDelete,
  } = useSelector((state: RootState) => state.alerts);

  const appointmentsOptions = appointmentsListForTestsResults?.map(
    (appointment) => ({
      key: appointment.id,
      value: JSON.stringify({
        title: appointment.title,
        scheduled_time: appointment.scheduled_time
      })
    })
  );

  const onSelectAppointment = async (option: any) => {
    setFieldValue('appointment', option);
    setIsTestsResultsLoading(true);
    dispatch(setSelectedAppointmentIdForTestResults(option.key));

    try {
      const appointmentTestResults =
        await getBiomarkersIdsExistingByAppointment(option.key);
      dispatch(
        setMemberBiomarkersResultsByAppointment(
          appointmentTestResults.biomarker_ids
        )
      );
    } catch (error) {
      console.error(error);
    } finally {
      setIsTestsResultsLoading(false);
    }
  };

  const onAddBiomarkerClick = () => {
    const newBiomarker = {
      id: Math.random().toString(),
      name: { key: '', value: '' },
      summary: '',
      value: '',
      measurement_unit: ''
    };
    setFieldValue('biomarkers', [...values.biomarkers, newBiomarker]);
  };

  const isImportAndAddBtnDisabled = useMemo(() => {
    return (
      !values.appointment.key ||
      values.biomarkers?.length === activeBiomarkersList.length
    );
  }, [values?.appointment?.key, values?.biomarkers?.length, activeBiomarkersList]);

  const isSubmitBtnDisabled = useMemo(() => {
    return (
      !values.appointment.key ||
      values.biomarkers?.length === 0 ||
      values.biomarkers?.some(
        (biomarker) => !biomarker.name || !biomarker.value
      )
    );
  }, [values?.appointment?.key, values?.biomarkers]);

  const onImportHL7Click = () => {
    dispatch(setIsSelectHL7ModalOpened(true));
  };

  const onCloseSelectHL7Modal = () => {
    dispatch(setIsSelectHL7ModalOpened(false));
  };

  const onUploadCSVClick = () => {
    csvFileInputRef.current?.click();
  };

  const handleCSVFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if file is CSV or Excel
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!['.csv', '.xlsx', '.xls'].includes(fileExtension)) {
      alert('Please select a CSV or Excel file (.csv, .xlsx, .xls)');
      return;
    }

    setIsUploadingCSV(true);
    try {
      const result = await parseSimpleBiomarkerFile(file);
      
      if (!result.success) {
        alert(`Error parsing file: ${result.errors.join(', ')}`);
        return;
      }

      if (result.data.length === 0) {
        alert('No valid biomarker data found in the file');
        return;
      }

      // Convert simple format to the expected format
      const testResults = result.data.map(item => ({
        testName: item.biomarkerName,
        value: item.isBinary && item.binaryValue ? item.binaryValue : item.value,
        unit: '',
        referenceRange: '',
        status: '',
        notes: item.summary ? [item.summary] : [],
        isBinary: item.isBinary || false,
        binaryValue: item.binaryValue
      }));

      const parsedData = {
        testResults,
        memberInfo: {
          id: '',
          name: '',
          dateOfBirth: '',
          gender: ''
        }
      };

      await parseCSVfileToBiomarkers(parsedData);
    } catch (error) {
      console.error('Error processing CSV file:', error);
      alert('Error processing file. Please try again.');
    } finally {
      setIsUploadingCSV(false);
      // Reset the file input
      if (csvFileInputRef.current) {
        csvFileInputRef.current.value = '';
      }
    }
  };


  const parseHL7fileToBiomarkers = async (res: any) => {
    const testResults = res.testResults || [];
    let freshBiomarkersList = activeBiomarkersList;
    
    try {
      const { getBiomarkersList } = await import('../../../../api/biomarkers');
      const biomarkersResponse = await getBiomarkersList();
      freshBiomarkersList = filterActiveBiomarkers(biomarkersResponse.biomarkers);
    } catch (error) {
      console.error('Failed to load fresh biomarkers, using cached:', error);
    }

    const mapped: any[] = [];
    const unmapped: any[] = [];

    testResults.forEach((result: any) => {
      const testName = result.testName;
      const value = result.value;

      const biomarker = freshBiomarkersList.find(
        (b) => b.name === testName || b.lab_bm_id === testName
      );

      if (biomarker && value) {
        // Apply the same logic as availableBiomarkerOptions in SingleBiomarkerTestResult
        const isSelected = values.biomarkers?.some(
          (b: any) => b.name?.key === biomarker!.id
        );
        const isExisting = memberBiomarkersExistingIdsByAppointment?.includes(parseInt(String(biomarker!.id)));
        const isDuplicate = isSelected || isExisting;

        if (!isDuplicate) {
          mapped.push({
            id: Math.random().toString(),
            measurement_unit: biomarker.measurement_unit || '',
            name: { 
              key: biomarker!.id, 
              value: biomarker.name,
              type: biomarker.biomarker_type
            },
            value,
            summary: ''
          });
        }
      } else {
        unmapped.push(result);
      }
    });

    // Replace empty biomarker with imported data, or add to existing biomarkers
    const hasEmptyBiomarker = values.biomarkers.length === 1 && values.biomarkers[0].id === '';
    const existingBiomarkers = hasEmptyBiomarker ? [] : values.biomarkers;
    setFieldValue('biomarkers', [...existingBiomarkers, ...mapped]);
    setUnmappedTests(unmapped);
  };

  const parseCSVfileToBiomarkers = async (res: any) => {
    const testResults = res.testResults || [];
    let freshBiomarkersList = activeBiomarkersList;
    
    try {
      const { getBiomarkersList } = await import('../../../../api/biomarkers');
      const biomarkersResponse = await getBiomarkersList();
      freshBiomarkersList = filterActiveBiomarkers(biomarkersResponse.biomarkers);
    } catch (error) {
      console.error('Failed to load fresh biomarkers, using cached:', error);
    }

    const mapped: any[] = [];
    const unmapped: any[] = [];

    testResults.forEach((result: any) => {
      const testName = result.testName;
      const value = result.value;

      // Only match exact names or lab IDs - no partial matching
      const biomarker = freshBiomarkersList.find(
        (b) => b.name === testName || b.lab_bm_id === testName ||
               b.name.toLowerCase() === testName.toLowerCase()
      );

      if (biomarker && value) {
        // Apply the same logic as availableBiomarkerOptions in SingleBiomarkerTestResult
        const isSelected = values.biomarkers?.some(
          (b: any) => b.name?.key === biomarker!.id
        );
        const isExisting = memberBiomarkersExistingIdsByAppointment?.includes(parseInt(String(biomarker!.id)));
        const isDuplicate = isSelected || isExisting;

        if (!isDuplicate) {
          // Handle binary values - if the biomarker is binary type and we have a binary value
          let finalValue = value;
          if (biomarker.biomarker_type === 'BINARY' && result.isBinary && result.binaryValue) {
            finalValue = result.binaryValue; // Use POSITIVE or NEGATIVE directly
          }

          mapped.push({
            id: Math.random().toString(),
            measurement_unit: biomarker.measurement_unit || '',
            name: { 
              key: biomarker!.id, 
              value: biomarker.name,
              type: biomarker.biomarker_type
            },
            value: finalValue,
            summary: result.notes?.join('; ') || ''
          });
        }
      } else {
        unmapped.push(result);
      }
    });

    // Replace empty biomarker with imported data, or add to existing biomarkers
    const hasEmptyBiomarker = values.biomarkers.length === 1 && values.biomarkers[0].id === '';
    const existingBiomarkers = hasEmptyBiomarker ? [] : values.biomarkers;
    setFieldValue('biomarkers', [...existingBiomarkers, ...mapped]);
    setUnmappedTests(unmapped);
  };

  const btnText =
    mode === 'create' ? 'Add biomarker results' : 'Update biomarker results';

  const onConfirmDeleteTestResultClick = async () => {
    try {
      if (selectedTestResultIdToDelete)
        await deleteBiomarkerResult([selectedTestResultIdToDelete]);
      dispatch(changeDeleteSingleBiomarkerResultModalVisibility(false));
      dispatch(setSelectedTestResultIdToDelete(null));
      const biomarkers = values.biomarkers;
      const updatedBiomarkers = biomarkers.filter(
        (biomarker: any) => biomarker!.id !== selectedTestResultIdToDelete
      );
      setFieldValue('biomarkers', updatedBiomarkers);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className='tests-modal-content-form'>
        <AutoSelectAppointment
          isOpen={isOpen}
          selectedAppointmentIdForTestResults={
            selectedAppointmentIdForTestResults!
          }
          appointmentsOptions={appointmentsOptions}
          onSelectAppointment={onSelectAppointment}
        />
        <div
          className='select-container'
          style={{
            marginBottom: values.appointment.key ? '0px' : '124px'
          }}
        >
          <Select
            name='appointment'
            title='Appointment *'
            placeholder='Choose appointment'
            options={appointmentsOptions}
            onChange={(option) => onSelectAppointment(option)}
            selectedOption={values.appointment}
            isError={false}
            isObjectValue={true}
          />
        </div>
        {isTestsResultsLoading && (
          <div className='loader-container'>
            <Loader />
          </div>
        )}
        <div className='appointment-id-container'>
          <div className='section-title'>Appointment ID</div>
          <div>{appointmentIdToEditForTestResults}</div>
        </div>
        {values.appointment.key && !isTestsResultsLoading && (
          <div className='biomarkers-results-container'>
            {values.biomarkers?.map((result, i) => (
              <SingleBiomarkerTestResult
                key={result.id}
                biomarkerIndex={i}
                biomarkerData={result}
                openedAccordion={openedBiomarker!}
                setOpenedBiomarker={setOpenedBiomarker}
                mode={mode}
              />
            ))}
          </div>
        )}
        {!!unmappedTests.length && (
          <div className='unmapped-tests'>
            <div className='section-title'>Unrecognized Biomarkers</div>
            <div className='unmapped-tests-list'>
              {unmappedTests.map((test: any) => (
                <div key={test.id} className='unmapped-test-item'>
                  <span>{test.testName}</span>
                  <span>{test.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className='form-btns-container'>
          {mode === 'create' && (
            <div className='top-btns-wrapper'>
              <Button
                isSecondary={true}
                text='Add biomarker'
                onClick={onAddBiomarkerClick}
                backgroundColor='#a8a7a9'
                color='#FFFFFF'
                isDisabled={isImportAndAddBtnDisabled}
                width='100%'
              />
              <Button
                text='Import HL7 file'
                backgroundColor='#a8a7a9'
                color='#FFFFFF'
                onClick={onImportHL7Click}
                isDisabled={isImportAndAddBtnDisabled}
              />
              <Button
                text='Import CSV/Excel file'
                backgroundColor='#a8a7a9'
                color='#FFFFFF'
                onClick={onUploadCSVClick}
                isDisabled={isImportAndAddBtnDisabled || isUploadingCSV}
                isLoading={isUploadingCSV}
              />
            </div>
          )}
          <div className='bottom-btns-wrapper'>
            <Button
              type='submit'
              text={btnText}
              isDisabled={isSubmitBtnDisabled}
              isLoading={isSubmitting}
            />
          </div>
        </div>
        {isDeleteSingleBiomarkerResultModalVisible && (
          <ConfirmModal
            isOpen={isDeleteSingleBiomarkerResultModalVisible}
            onClose={() =>
              dispatch(changeDeleteSingleBiomarkerResultModalVisibility(false))
            }
            onConfirmClick={onConfirmDeleteTestResultClick}
            title='Delete biomarker result?'
            subtitle='Are you sure you want to delete this biomarker result? The result will be permanently deleted.'
          />
        )}
        {isSelectHL7ModalOpened && (
          <SelectHL7Modal
            onClose={onCloseSelectHL7Modal}
            onSelect={(res) => parseHL7fileToBiomarkers(res)}
          />
        )}
        {/* Hidden file input for direct CSV/Excel upload */}
        <input
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleCSVFileChange}
          ref={csvFileInputRef}
          style={{ display: 'none' }}
        />
      </form>
    </div>
  );
};

export default TestResultsModalForm;